import * as THREE from 'three';
import { CARD_CORNER_RATIO } from '../../lib/cardCorner.js';
import { resolveLocalImageUrl } from '../localApi';
import { SCENE_ENV_MAP_INTENSITY } from './lightingRig';
import { corsTextureUrl } from '../cardImageUrl';
import { FOIL_VERTEX_SHADER, FOIL_FRAGMENT_SHADER } from '../sorcery/foilShader.js';
import { FOIL_WHITE_ZONES, foilZonesForCard } from '../sorcery/foilZones.js';
import { buildFoilUniforms, getFoilMatcap, loadFoilTextures, foilPlaceholderTexture } from '../sorcery/foilMaterial.js';
import { k } from '../../localization/t';

const CARD_SCALE = 1.75;
// Tokens render at half the linear size of a regular card (¼ of the
// area) so they're instantly distinguishable on the board. Shared with
// visualEffects so status VFX scale down to match.
const TOKEN_CARD_SCALE = 0.5;
const CARD_WIDTH = 6.35 * CARD_SCALE;
const CARD_HEIGHT = 8.89 * CARD_SCALE;
// Visible thickness. Cards behave like real 3D objects so stacks are
// readable instead of z-fighting paper-thin slabs.
const CARD_THICKNESS = 0.18 * CARD_SCALE;
const CARD_EDGE_COLOR = 0x111111;

const textureLoader = new THREE.TextureLoader();
textureLoader.crossOrigin = 'anonymous';
// Board card-art texture cache. Unlike foilStage's cache (where "in use" =
// referenced by a live ctx.entry, so safe to evict), these textures are held
// directly by live board meshes via material.map and there's no cheap registry
// of which URLs are still mounted. Disposing an in-use texture would blank a
// visible card, so this cache is NOT LRU-evicted (#5). A single match touches a
// bounded set of cards (two decks + tokens), so unbounded growth is bounded in
// practice; the leak that matters (per-card foil ShaderMaterials with their own
// uncached textures) is disposed on prune in updateFoilSheens instead.
// ponytail: intentionally uncapped. A safe eviction needs live-mesh tracking
// that isn't worth building for a per-match-bounded cache.
const textureCache = new Map();

// Shared helper. Traces a rounded rectangle path (half-width `hw`,
// half-height `hh`, corner radius `cr`) onto a THREE.Shape or
// THREE.Path. Used by the hover stroke and the accessibility
// ownership outline so both ring geometries stay consistent.
function traceRoundedRect(pathOrShape, hw, hh, cr) {
  pathOrShape.moveTo(-hw + cr, -hh);
  pathOrShape.lineTo(hw - cr, -hh);
  pathOrShape.quadraticCurveTo(hw, -hh, hw, -hh + cr);
  pathOrShape.lineTo(hw, hh - cr);
  pathOrShape.quadraticCurveTo(hw, hh, hw - cr, hh);
  pathOrShape.lineTo(-hw + cr, hh);
  pathOrShape.quadraticCurveTo(-hw, hh, -hw, hh - cr);
  pathOrShape.lineTo(-hw, -hh + cr);
  pathOrShape.quadraticCurveTo(-hw, -hh, -hw + cr, -hh);
}

function buildCardRingGeometry(thickness) {
  const outerW = CARD_WIDTH / 2 + thickness;
  const outerH = CARD_HEIGHT / 2 + thickness;
  const innerW = CARD_WIDTH / 2;
  const innerH = CARD_HEIGHT / 2;
  // Concentric with the card corner: the hole IS the card outline.
  const rInner = CARD_CORNER_RADIUS;
  const rOuter = rInner + thickness;
  const outerShape = new THREE.Shape();
  traceRoundedRect(outerShape, outerW, outerH, rOuter);
  const holePath = new THREE.Path();
  traceRoundedRect(holePath, innerW, innerH, rInner);
  outerShape.holes.push(holePath);
  return new THREE.ShapeGeometry(outerShape);
}

/**
 * Flat rounded-rectangle ring mesh aligned to the card face. Used by
 * the accessibility ownership outline (persistent, colored per
 * player). Sits slightly below the hover stroke's z-layer so the two
 * don't z-fight when a hovered card is also outlined for ownership.
 */
// Pick-target halo: same rounded card-ring as the ownership outline,
// but bolder and mounted a hair above it so the two never z-fight when
// a card is both owned-outlined and an eligible pick target. Added as a
// CHILD of the card mesh, so it tracks position / rotation / tap in
// world space and reads identically from every camera angle. Unlike
// the old DOM rectangles, which were axis-aligned screen boxes that
// ignored the card's rounded corners and swam during camera moves.
export function createTargetHaloMesh(color) {
  const geo = buildCardRingGeometry(0.45);
  const mat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.9,
    side: THREE.DoubleSide,
    depthTest: true,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.z = 0.022;
  mesh.renderOrder = 10;
  return mesh;
}

export function createOwnershipOutlineMesh(thickness, color) {
  const geo = buildCardRingGeometry(thickness);
  const mat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.95,
    side: THREE.DoubleSide,
    depthTest: true,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.z = 0.015;
  mesh.renderOrder = 9;
  return mesh;
}

let spellbookBackTexture = null;
let atlasBackTexture = null;

// Renderer reference used to eagerly upload textures to the GPU as
// soon as their image data finishes decoding. Without this, every new
// card's texture is uploaded lazily on its first appearance in a
// render call. Which causes 10ms+ frame spikes whenever multiple
// cards land on the table at once. tableScene calls setTextureRenderer
// on init to wire this up.
let textureRenderer = null;
export function setTextureRenderer(renderer) {
  textureRenderer = renderer;
}

// When a card image fails to load (server timeout, 5xx from blob,
// network abort), we used to leave the THREE.Texture in its
// uninitialized state and keep it in the cache. The mesh would render
// fully transparent (alphaTest discards a no-data sample) and every
// future card with the same URL got the same broken handle, so the
// failure was permanent until the renderer restarted. The error path
// now evicts the cache entry so subsequent calls retry, and. If the
// caller supplied a `fallbackTexture` whose image has already decoded.
// swaps that image onto the broken texture so the card visibly shows
// a card-back instead of disappearing.
// Redraw a decoded card image with its corners clipped to the app-wide card
// corner. The scans ship with rounded transparent corners baked in at a radius
// that differs by finish (standard ~7.6% of the width, foil ~8.5%), so a flat
// quad showing the raw art (pile top face, opponent hand faces, the backs)
// would round differently from the extruded table card and from each other.
function roundCardImage(image) {
  const w = image?.naturalWidth || image?.width;
  const h = image?.naturalHeight || image?.height;
  if (!w || !h || typeof document === 'undefined') return image;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.beginPath();
  ctx.roundRect(0, 0, w, h, CARD_CORNER_RATIO * Math.min(w, h));
  ctx.clip();
  ctx.drawImage(image, 0, 0);
  // Retain the loaded pixels' origin for scene diagnostics after canvas clipping.
  canvas.dataset.sourceUrl = image.currentSrc || image.src || image.dataset?.sourceUrl || '';
  return canvas;
}

function loadTexture(url, fallbackTexture = null, { card = true } = {}) {
  if (textureCache.has(url)) return textureCache.get(url);
  const tex = textureLoader.load(
    corsTextureUrl(url),
    (loadedTex) => {
      if (card) {
        loadedTex.image = roundCardImage(loadedTex.image);
        loadedTex.needsUpdate = true;
      }
      if (textureRenderer) {
        try { textureRenderer.initTexture(loadedTex); } catch {}
      }
    },
    undefined,
    (err) => {
      console.warn(`[loadTexture] failed: ${url}`, err?.message || err);
      textureCache.delete(url);
      if (fallbackTexture?.image) {
        tex.image = fallbackTexture.image;
        tex.needsUpdate = true;
      }
    },
  );
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  textureCache.set(url, tex);
  return tex;
}

export function setCardBackUrls(spellbookUrl, atlasUrl) {
  spellbookBackTexture = loadTexture(spellbookUrl);
  atlasBackTexture = loadTexture(atlasUrl);
  // The atlas card back image is landscape (horizontal), but it's mapped
  // onto a portrait card geometry that gets rotated 90° by the mesh.
  // Pre-rotate the texture -90° so it aligns correctly after the mesh
  // rotation. Without this the back appears sideways on the board,
  // in hand, and on the pile.
  atlasBackTexture.center.set(0.5, 0.5);
  atlasBackTexture.rotation = Math.PI / 2;
}

function getBackTexture(isSite, forceSpellbook = false) {
  if (forceSpellbook) return spellbookBackTexture;
  return isSite ? atlasBackTexture : spellbookBackTexture;
}

// Shared geometry and edge material. All cards have identical
// dimensions, so one instance is reused across every card mesh.
let _sharedCardGeo = null;
let _sharedEdgeMat = null;
let _sharedPaperBump = null;

// Corner radius of the printed card art, measured in world units.
// Matches the rounded ring used for the hover stroke outline so the
// geometry follows the same silhouette as the existing visual
// treatment. Every card art has this same rounding baked in. A
// sharp-cornered geometry underneath is what made the paper-edge
// treatment look wrong.
const CARD_CORNER_RADIUS = CARD_WIDTH * CARD_CORNER_RATIO;

function buildCardShape() {
  const w = CARD_WIDTH / 2;
  const h = CARD_HEIGHT / 2;
  const r = CARD_CORNER_RADIUS;
  const shape = new THREE.Shape();
  shape.moveTo(-w + r, -h);
  shape.lineTo(w - r, -h);
  shape.quadraticCurveTo(w, -h, w, -h + r);
  shape.lineTo(w, h - r);
  shape.quadraticCurveTo(w, h, w - r, h);
  shape.lineTo(-w + r, h);
  shape.quadraticCurveTo(-w, h, -w, h - r);
  shape.lineTo(-w, -h + r);
  shape.quadraticCurveTo(-w, -h, -w + r, -h);
  return shape;
}

// Rounded-corner extruded prism. Three material groups so one mesh
// can carry the front art, the back texture, and the dark paper edge
// In that order:
//   0: front face (card art)
//   1: back face (card-back pattern)
//   2: side walls (edge material)
// ExtrudeGeometry naturally produces two groups (caps + walls) where
// caps bundle front+back in one index range. We split that range in
// half. ExtrudeGeometry emits back-cap triangles first, then front-
// cap triangles. And remap material indices to match the caller's
// expected material array.
function getCardGeometry() {
  if (_sharedCardGeo) return _sharedCardGeo;

  const halfW = CARD_WIDTH / 2;
  const halfH = CARD_HEIGHT / 2;
  const shape = buildCardShape();

  const UVGenerator = {
    generateTopUV(geometry, vertices, indexA, indexB, indexC) {
      return [
        new THREE.Vector2(
          (vertices[indexA * 3] + halfW) / CARD_WIDTH,
          (vertices[indexA * 3 + 1] + halfH) / CARD_HEIGHT,
        ),
        new THREE.Vector2(
          (vertices[indexB * 3] + halfW) / CARD_WIDTH,
          (vertices[indexB * 3 + 1] + halfH) / CARD_HEIGHT,
        ),
        new THREE.Vector2(
          (vertices[indexC * 3] + halfW) / CARD_WIDTH,
          (vertices[indexC * 3 + 1] + halfH) / CARD_HEIGHT,
        ),
      ];
    },
    generateSideWallUV() {
      return [
        new THREE.Vector2(0, 0),
        new THREE.Vector2(1, 0),
        new THREE.Vector2(1, 1),
        new THREE.Vector2(0, 1),
      ];
    },
  };

  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: CARD_THICKNESS,
    bevelEnabled: false,
    curveSegments: 12,
    UVGenerator,
  });

  // Extrude runs 0→depth; shift so the card centers on its origin
  // (matches the old BoxGeometry convention and the stacking math
  // everywhere else).
  geo.translate(0, 0, -CARD_THICKNESS / 2);

  const origGroups = geo.groups.slice();
  const capGroup = origGroups.find((g) => g.materialIndex === 0);
  const wallGroup = origGroups.find((g) => g.materialIndex === 1);
  const capHalf = capGroup.count / 2;

  geo.clearGroups();
  // Back cap first (z=0 before translate → -CARD_THICKNESS/2), front second.
  geo.addGroup(capGroup.start, capHalf, 1);
  geo.addGroup(capGroup.start + capHalf, capHalf, 0);
  geo.addGroup(wallGroup.start, wallGroup.count, 2);

  // UVGenerator yields the same UVs for both caps. Looking at the back
  // face from -Z the texture would read mirrored in X. Flip the
  // back-cap vertex UVs once so the back texture appears upright.
  //
  // ExtrudeGeometry is non-indexed (each triangle has three unique
  // vertices in the position buffer, no index buffer), so group.start
  // and group.count reference vertex ranges directly. No index
  // dereference needed. Iterating the back-cap vertex range and
  // rewriting U in place is enough.
  const uvs = geo.attributes.uv.array;
  const backVertEnd = capGroup.start + capHalf;
  for (let vi = capGroup.start; vi < backVertEnd; vi++) {
    uvs[vi * 2] = 1 - uvs[vi * 2];
  }
  geo.attributes.uv.needsUpdate = true;

  _sharedCardGeo = geo;
  return _sharedCardGeo;
}

// Real card stock has a visible paper-edge silhouette. Previously this
// was rendered with opacity: 0 which flattened the read of the card
// against the table. Nothing caught the edge light from the side. A
// dark, matte edge matches standard printed TCG cards (the thin dark
// strip you see on a Magic/Sorcery/FaB card when you tilt it).
function getEdgeMaterial() {
  if (!_sharedEdgeMat) {
    _sharedEdgeMat = new THREE.MeshStandardMaterial({
      color: 0x0a0a0a,
      roughness: 0.85,
      metalness: 0.0,
      // Equal-depth fragments overdraw. Combined with mesh.renderOrder
      // assignment in GameBoard's _applyComputedY, this gives strict
      // GPU-level draw-order control for stacked cards regardless of
      // their actual Y values. Without this, two coplanar cards with
      // different renderOrder would have the second one's fragments
      // depth-tested out, leaving stacking visually broken.
      depthFunc: THREE.LessEqualDepth,
    });
  }
  return _sharedEdgeMat;
}

// Procedural cardstock bump. Real card paper isn't a mirror. Its
// surface has tiny high-frequency variation that catches specular
// highlights unevenly. Without this the printed art looks like a
// sticker on glass. A small canvas of per-pixel noise, tiled a handful
// of times across the card, is enough for MeshPhysicalMaterial's
// bumpMap to produce believable micro-relief under the clearcoat.
// Generated once per session and shared across every card material.
function getPaperBumpTexture() {
  if (_sharedPaperBump) return _sharedPaperBump;
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const img = ctx.createImageData(size, size);
  const data = img.data;
  // Noise centered on mid-gray so the bump doesn't bias the surface
  // up or down overall. Only sideways variation. ±18 amplitude is
  // enough to read under the clearcoat lobe without looking noisy on
  // the card art itself.
  for (let i = 0; i < data.length; i += 4) {
    const n = 128 + (Math.random() - 0.5) * 36;
    data[i] = n;
    data[i + 1] = n;
    data[i + 2] = n;
    data[i + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  // Repeat a few times across the face so the grain scale reads as
  // "paper fibre" rather than "coarse static".
  tex.repeat.set(5, 7);
  tex.anisotropy = 4;
  tex.needsUpdate = true;
  _sharedPaperBump = tex;
  return _sharedPaperBump;
}

// ── Board foil: shared ShaderMaterial (matches inspector / hand) ──
// The board renders foils with the SAME foilShader.js used by
// FoilCanvas so a foil reads identically in-match and in the inspector.
// FoilCanvas owns its own renderer (LinearSRGB output + NoColorSpace
// textures); the board shares the main scene renderer, which outputs
// sRGB. So here uTex keeps the board's normal SRGBColorSpace (Three
// linearizes on sample, the shader works linear, the renderer
// re-encodes on output. Matching neighbouring non-foil cards). The
// matcap + foil map are DATA, not colour, so they stay NoColorSpace.

// Build the board foil front-cap material from the shared foil shader,
// mirroring FoilCanvas's uniform set exactly (via foilMaterial.js).
// `frontTexture` is the already-cached board card texture (SRGBColorSpace);
// `imageUrl` is the root-relative art URL used to derive the sibling foil map.
function buildFoilShaderMaterial(frontTexture, imageUrl, zones = FOIL_WHITE_ZONES, useTexAlpha = false) {
  // Sites (horizontal) + avatars use a different frame, so they get their own
  // zone rects; spells/minions get the name-plate/text-box zones.
  // useTexAlpha: take the card art's own alpha (its baked rounded-corner
  // transparency) as the output alpha. Used for the cemetery pile's FLAT box
  // face (no rounded geometry to follow) so its corners round EXACTLY like the
  // non-foil card art. Field cards leave it off; their rounded ExtrudeGeometry
  // already shapes the opaque face.
  const uniforms = buildFoilUniforms({ zones: zones || FOIL_WHITE_ZONES });
  if (useTexAlpha) uniforms.uUseTexAlpha.value = 1;
  // Board path: run the scene-lit branch of the foil shader so the highlight
  // tracks the real camera + the scene's directional light, and the foil dims/
  // warms with the day-night cycle. uLightDir/uLightColor/uAmbient are fed per
  // frame by updateFoilSheens. (DOM foil callers leave uSceneLit at 0.)
  // Use the proven DOM self-lit foil model on the board too (uSceneLit=0): it
  // shows the full coloured metal/holo layer and reacts to the camera because
  // updateFoilSheens feeds uTilt from the card->camera view angle every frame.
  // The uSceneLit=1 scene-lit branch killed the metal layer (relief survived,
  // colour didn't), so it's disabled until that lighting model is reworked.
  uniforms.uSceneLit.value = 0;
  // Deck-builder parity: run the foil math on RAW sRGB bytes (NoColorSpace),
  // exactly like FoilCanvas/foilStage, then sRGB-decode the output (uOutputLinear)
  // so the scene renderer's sRGB encode cancels and the result matches the deck
  // builder. uTex starts on the board's SRGB texture for an immediate image, then
  // swaps to a NoColorSpace load below (same url, usually warm in cache).
  uniforms.uOutputLinear.value = 1;
  uniforms.uTex.value = frontTexture;
  const imgW = frontTexture.image?.width || 744;
  const imgH = frontTexture.image?.height || 1039;
  uniforms.uTexel.value.set(1 / imgW, 1 / imgH);
  // Re-point uMatcap when the shared studio matcap decodes.
  getFoilMatcap((tex) => { uniforms.uMatcap.value = tex; });

  // Card art as a NoColorSpace (raw-passthrough) texture so the foil math sees
  // the same byte values the deck builder does, + the baked foil map. Reuse the
  // crossOrigin loader. The foil map 404 keeps the runtime-derived fallback.
  loadFoilTextures(imageUrl, {
    loader: textureLoader,
    texColorSpace: THREE.NoColorSpace,
    onTex: (tex) => {
      uniforms.uTex.value = tex;
      if (tex.image) uniforms.uTexel.value.set(1 / tex.image.width, 1 / tex.image.height);
    },
    onFoilMap: (tex) => { uniforms.uFoilMap.value = tex; uniforms.uHasFoilMap.value = 1; },
    onDepthMap: (tex) => { uniforms.uDepthMap.value = tex; uniforms.uHasDepthMap.value = 1; },
  });

  // The foil shader always writes alpha = 1.0 (FoilCanvas relies on CSS
  // border-radius to clip its corners. The board instead has rounded
  // ExtrudeGeometry, so the opaque face already follows the card
  // silhouette). Render opaque with the same polygonOffset + depthFunc
  // the non-foil front cap uses, so stacked foils sort identically to
  // regular cards (transparent:true would drop depthWrite and break
  // coplanar stacking order).
  return new THREE.ShaderMaterial({
    uniforms,
    vertexShader: FOIL_VERTEX_SHADER,
    fragmentShader: FOIL_FRAGMENT_SHADER,
    // Opaque by default (field cards rely on depthWrite for coplanar stacking).
    // When useTexAlpha is on the shader outputs the art's alpha (rounded corners
    // transparent), so the pile top face must render transparent for the corners
    // to show. The pile is a single top face (not coplanar-stacked), so safe.
    transparent: useTexAlpha,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
    depthFunc: THREE.LessEqualDepth,
  });
}

// Registry of live board foil ShaderMaterials + their host mesh, ticked
// each frame by updateFoilSheens (uTime + view-angle uTilt).
const foilShaderEntries = [];

// Dirty flag for the foil mesh set: set true whenever a foil entry is pushed
// (createCardMesh) or pruned (updateFoilSheens). tableScene reads + clears it
// via consumeFoilMeshesDirty() so it only rebuilds the bloom selection Set when
// the set actually changed, instead of allocating a fresh array every frame.
let _foilMeshesDirty = true;

// Live list of foil card meshes, for the scene's SelectiveBloomEffect to
// EXCLUDE from bloom (so board foils match the un-bloomed deck-builder look).
// updateFoilSheens prunes detached meshes each frame, so call this after it.
// Reuses a persistent array (rebuilt in place) to avoid per-call allocation.
const _foilMeshes = [];
export function getFoilMeshes() {
  _foilMeshes.length = 0;
  for (const e of foilShaderEntries) _foilMeshes.push(e.mesh);
  return _foilMeshes;
}

// Returns true (and clears the flag) if the foil mesh set changed since the
// last call. tableScene only re-syncs bloomEffect.selection when this is true.
export function consumeFoilMeshesDirty() {
  if (!_foilMeshesDirty) return false;
  _foilMeshesDirty = false;
  return true;
}

// Kept for backward compat. TableScene calls updateFoilSheens().
let foilSheenTime = 0;

// Scratch objects reused per frame (no per-frame allocation in the tick).
const _foilCamPos = new THREE.Vector3();
const _foilCardPos = new THREE.Vector3();
const _foilToCam = new THREE.Vector3();
const _foilRight = new THREE.Vector3();
const _foilUp = new THREE.Vector3();
const _foilMat = new THREE.Matrix4();
const _foilLightDir = new THREE.Vector3();
const _foilLightWorld = new THREE.Vector3();
const _foilTargetWorld = new THREE.Vector3();

// Dispose a pruned board foil entry's GPU resources (#7 leak fix). The foil
// ShaderMaterial's uTex/uFoilMap are loaded fresh per card (NoColorSpace, not in
// any shared cache), so they must be disposed with the material. Guard against
// disposing the app-wide shared placeholder or matcap (referenced by every foil)
// and against a double-dispose (set .value to null after).
function disposeFoilEntry(entry) {
  const mat = entry.material;
  if (!mat || mat._foilDisposed) return;
  mat._foilDisposed = true;
  const u = mat.uniforms;
  const placeholder = foilPlaceholderTexture();
  const matcap = u.uMatcap?.value;
  const disposeTex = (slot) => {
    const tex = slot?.value;
    if (tex && tex !== placeholder && tex !== matcap && typeof tex.dispose === 'function') {
      tex.dispose();
    }
    if (slot) slot.value = null;
  };
  disposeTex(u.uTex);
  disposeTex(u.uFoilMap);
  disposeTex(u.uDepthMap);
  mat.dispose();
}

// `camera` is optional so older call sites (and tests) keep working; when
// supplied, board foils get a subtle view-angle tilt so the sheen shifts
// as the board camera orbits/pans. There's no per-card pointer on the
// board. The mapping projects the card→camera direction onto the card's
// own right/up axes and scales to roughly [-0.5, 0.5], matching the
// ((mx/100)-0.5) range FoilCanvas feeds from pointer position. Stable
// (derived purely from transforms, no noise) so it never jitters.
export function updateFoilSheens(dt, camera = null, directional = null, ambient = null) {
  foilSheenTime += dt;
  if (camera) camera.getWorldPosition(_foilCamPos);
  // Scene lighting fed into the foil shader's uSceneLit branch so the board
  // foils dim/warm with the day-night cycle and the highlight tracks the real
  // directional light. uLightDir is the WORLD direction TOWARD the light, i.e.
  // from the light's target toward its position (Three's DirectionalLight emits
  // from position toward target. The surface receives it along that axis, and
  // the shader's light-toward convention is the negation).
  // These three (uLightDir/uLightColor/uAmbient) are SCENE-GLOBAL, identical for
  // every foil, so the values are computed ONCE here per frame; the per-entry
  // loop below only copies them in. (The board runs uSceneLit=0, so the shader
  // never reads them. These writes are presently dead, kept cheap-and-fed for
  // when scene-lit foils are re-enabled.)
  let haveLight = false;
  let lightR = 0, lightG = 0, lightB = 0;
  if (directional) {
    directional.getWorldPosition(_foilLightWorld);
    directional.target.getWorldPosition(_foilTargetWorld);
    _foilLightDir.subVectors(_foilLightWorld, _foilTargetWorld).normalize();
    lightR = directional.color.r * directional.intensity;
    lightG = directional.color.g * directional.intensity;
    lightB = directional.color.b * directional.intensity;
    haveLight = true;
  }
  let haveAmbient = false;
  let ambR = 0, ambG = 0, ambB = 0;
  if (ambient) {
    ambR = ambient.color.r * ambient.intensity;
    ambG = ambient.color.g * ambient.intensity;
    ambB = ambient.color.b * ambient.intensity;
    haveAmbient = true;
  }
  // Prune entries whose card mesh has been removed from the scene
  // (disposed on leaving the table) so the registry doesn't grow over a
  // long match. A detached mesh has no parent. The foil ShaderMaterial and its
  // NoColorSpace uTex/uFoilMap textures are loaded outside any shared cache (via
  // loadFoilTextures in buildFoilShaderMaterial), so nothing else owns them.
  // dispose them here or they stay GPU-orphaned for the rest of the session.
  for (let i = foilShaderEntries.length - 1; i >= 0; i--) {
    if (!foilShaderEntries[i].mesh.parent) {
      disposeFoilEntry(foilShaderEntries[i]);
      foilShaderEntries.splice(i, 1);
      _foilMeshesDirty = true;
    }
  }
  for (const { material, mesh } of foilShaderEntries) {
    const u = material.uniforms;
    u.uTime.value = foilSheenTime;
    if (haveLight) {
      u.uLightDir.value.copy(_foilLightDir);
      u.uLightColor.value.set(lightR, lightG, lightB);
    }
    if (haveAmbient) {
      u.uAmbient.value.set(ambR, ambG, ambB);
    }
    if (!camera) continue;
    mesh.getWorldPosition(_foilCardPos);
    _foilToCam.subVectors(_foilCamPos, _foilCardPos).normalize();
    // Card local +Z is the art-facing axis; +X/+Y span the face. Read the
    // world-space face axes from the mesh's rotation.
    _foilMat.extractRotation(mesh.matrixWorld);
    _foilRight.set(1, 0, 0).applyMatrix4(_foilMat);
    _foilUp.set(0, 1, 0).applyMatrix4(_foilMat);
    const tx = THREE.MathUtils.clamp(_foilToCam.dot(_foilRight) * 0.5, -0.5, 0.5);
    const ty = THREE.MathUtils.clamp(_foilToCam.dot(_foilUp) * 0.5, -0.5, 0.5);
    material.uniforms.uTilt.value.set(tx, ty);
    material.uniforms.uFromCenter.value = Math.min(1, Math.hypot(tx, ty) * 2);
  }
}

// Coated-print finish shared by the card front and back face materials.
// The original finish (roughness 0.45, clearcoat 0.55, clearcoatRoughness
// 0.35) turned the scene's DirectionalLight into a localized mirror hotspot
// on flat-lying cards (valkenhall-qa#78: the lower-right corner washed out).
//
// qa#78 retest: broadening the lobes (the first fix) lowered the PEAK but
// raised the off-peak tail - lobe width redistributes specular energy, it
// doesn't remove it - so the localized hotspot became a board-wide brightness
// ramp (QA: left side brighter than right, direction moving with the
// time-of-day light). The cemetery pile proved the diagnosis: its default
// MeshStandardMaterial (roughness 1, no clearcoat) is mathematically
// position-independent and QA saw it unaffected. The durable fix reduces
// specular AMPLITUDE, which scales the term identically at every angle:
// `specularIntensity` shrinks the base-layer F0/F90 for direct AND env light,
// `clearcoat` linearly scales the coat lobe (its F0 is fixed at 0.04, so
// specularIntensity alone can't touch it). `envMapIntensity` is the shared
// SCENE_ENV_MAP_INTENSITY the marble table uses (the first fix copied its
// lobes but not this, leaving cards with 2.5x the table's flat env veil). The clearcoat plus the paper
// bump map keeps the coated-print look. cardMesh.finish.test.js bounds both
// the peak glare AND the across-board swing (peak minus off-peak), the term
// the peak-only guard missed.
export const CARD_FACE_FINISH = {
  roughness: 0.6,
  metalness: 0.0,
  clearcoat: 0.06,
  clearcoatRoughness: 0.65,
  specularIntensity: 0.15,
  envMapIntensity: SCENE_ENV_MAP_INTENSITY,
};

export function createCardMesh(cardInstance) {
  const geometry = getCardGeometry();
  const foiling = cardInstance.foiling || 'S';
  const isFoil = foiling === 'F' || foiling === 'R';

  const edgeMat = getEdgeMaterial();

  const imageUrl = resolveLocalImageUrl(cardInstance.imageUrl);

  // When a card arrives face-down from the opponent with sanitized
  // identity (no imageUrl), use the card-back texture as the front
  // map too. The mesh renders face-down either way, so the front map
  // is never visible. But loadTexture(undefined) would throw. The
  // map is replaced later if/when the card is revealed via card:flip.
  const fallbackBack = getBackTexture(
    cardInstance.isToken ? false : cardInstance.isSite,
    !!cardInstance._spellbookBack,
  );
  const frontTexture = imageUrl
    ? loadTexture(imageUrl, fallbackBack)
    : fallbackBack;

  // alphaTest makes the GPU discard pixels where the texture alpha is
  // below the threshold. This means the rounded-corner transparent
  // pixels in the card art are truly invisible. No edge material
  // bleeding through, and the shadow map follows the card shape
  // instead of casting a rectangular shadow from the box geometry.
  // polygonOffset biases the front face toward the camera in the
  // depth buffer so it consistently wins against the back face at
  // all zoom levels. Prevents z-fighting flicker.
  const frontBase = {
    map: frontTexture,
    transparent: true,
    alphaTest: 0.5,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
    // See getEdgeMaterial for the depthFunc rationale. This allows
    // mesh.renderOrder to deterministically resolve stacking order
    // between coplanar transparent cards.
    depthFunc: THREE.LessEqualDepth,
  };

  // Non-foil cards get a coated-print look. A real clearcoat so the
  // card reads as a glossy printed surface instead of a matte poster.
  // The bumpMap adds high-frequency specular variation so the gloss
  // breaks up like real cardstock when the card is tilted.
  // Foil cards render with the shared foilShader.js (same look as the
  // inspector / hand). If that setup throws we fall back to the plain lit
  // MeshPhysicalMaterial below, so a foil never renders blank (it just shows
  // as a normal card without the foil). `foilEntry` is registered after the
  // mesh exists so the per-frame tick can drive its uTime / view-angle uTilt.
  let frontMat = null;
  let foilEntry = null;
  if (isFoil && frontTexture) {
    try {
      frontMat = buildFoilShaderMaterial(frontTexture, imageUrl || '', foilZonesForCard(cardInstance));
      foilEntry = true;
    } catch (err) {
      console.warn('[cardMesh] foil ShaderMaterial setup failed, using fallback:', err?.message || err);
      frontMat = null;
    }
  }
  if (!frontMat) {
    // Non-foil cards (and any foil whose ShaderMaterial setup threw) get the
    // coated-print look. A foil that fails shader setup renders as a normal
    // lit card rather than blank.
    frontMat = new THREE.MeshPhysicalMaterial({
      ...frontBase,
      ...CARD_FACE_FINISH,
      bumpMap: getPaperBumpTexture(),
      bumpScale: 0.02,
    });
  }

  // Two-sided cards (Druid avatar etc.) carry the back-face art on
  // the instance. Show THAT instead of the universal card back when
  // the mesh flips. From the player's perspective the Druid "flip"
  // doesn't hide its identity, it reveals the other printed face, so
  // the geometric back of the mesh has to carry the back-face art.
  // Tokens always use the spellbook back regardless of their type.
  const backFaceUrl = cardInstance.backFaceImagePath || cardInstance.backFaceImageUrl;
  const backTexture = backFaceUrl
    ? loadTexture(resolveLocalImageUrl(backFaceUrl), fallbackBack)
    : fallbackBack;
  // Match the front's coated-print finish on the back too. Same
  // bumpMap, same clearcoat, so flipping a card doesn't reveal a
  // flat-looking reverse side.
  const backMat = backTexture
    ? new THREE.MeshPhysicalMaterial({
        map: backTexture,
        transparent: true,
        alphaTest: 0.5,
        ...CARD_FACE_FINISH,
        bumpMap: getPaperBumpTexture(),
        bumpScale: 0.02,
        depthFunc: THREE.LessEqualDepth,
      })
    : new THREE.MeshStandardMaterial({
        color: 0x1a1a2e,
        depthFunc: THREE.LessEqualDepth,
      });

  // Rounded-corner ExtrudeGeometry group order: [front cap, back cap, walls].
  // Group material indices are rewritten in getCardGeometry() so this
  // array order is authoritative.
  const materials = [frontMat, backMat, edgeMat];
  const mesh = new THREE.Mesh(geometry, materials);

  // Register board foil shader materials so updateFoilSheens ticks their
  // uTime + view-angle uTilt each frame (FoilCanvas drives these from its
  // own RAF loop; the board has no per-card loop).
  if (foilEntry) {
    foilShaderEntries.push({ material: frontMat, mesh });
    _foilMeshesDirty = true;
  }

  // Lay flat: rotate so +Z (front/art) faces up (+Y), face-down flips
  // 180° around X so the back art is up instead.
  const baseX = cardInstance.faceDown ? Math.PI / 2 : -Math.PI / 2;
  mesh.rotation.x = baseX;
  // baseZ must mirror animations.js `computeTargetZ` exactly. It's the
  // same visual target applied statically at spawn instead of animated.
  // Missing any contribution here (tapped, faceDown) desyncs the mesh
  // visual from card.tapped: the next tap toggles a flag whose target
  // rotation already equals the current intent, producing a silent
  // "tap X" log entry with no animation, and making state-synced
  // tapped/face-down cards render in the wrong orientation.
  let baseZ = 0;
  if (cardInstance.isSite) baseZ -= Math.PI / 2;
  if (cardInstance.rotated) baseZ += Math.PI;
  if (cardInstance.faceDown) baseZ += Math.PI;
  // Tap = a quarter-turn. A SITE rests landscape (-90), so its tap turns
  // TOWARD upright portrait (+90 -> 0). Never the -90 that composed to an
  // upside-down 180.
  if (cardInstance.tapped) baseZ += cardInstance.isSite ? Math.PI / 2 : -Math.PI / 2;
  mesh.rotation.z = baseZ;
  mesh.position.set(cardInstance.x, 0.05 + CARD_THICKNESS / 2, cardInstance.z);

  // Token cards render at 50% size so they're visually distinct from
  // regular cards on the board. The scale is applied to the mesh, not
  // the geometry, so physics, flip, and tap all still work normally.
  if (cardInstance.isToken) {
    mesh.scale.setScalar(TOKEN_CARD_SCALE);
  }

  // Hover stroke outline. A rounded-rect ring flush against the card
  // edge. Uses an extruded ring shape for reliable thickness (WebGL
  // ignores linewidth). Rendered above the ownership outline so a
  // hovered card still reads as "being pointed at".
  const strokeGeo = buildCardRingGeometry(0.25);
  const strokeMat = new THREE.MeshBasicMaterial({
    color: 0xd4a843,
    transparent: true,
    opacity: 0.85,
    side: THREE.DoubleSide,
    depthTest: true,
  });
  const strokeMesh = new THREE.Mesh(strokeGeo, strokeMat);
  strokeMesh.position.z = 0.02;
  strokeMesh.visible = false;
  strokeMesh.renderOrder = 10;
  mesh.add(strokeMesh);

  mesh.castShadow = true;
  mesh.receiveShadow = false;
  // intentEuler is the "should look like this" orientation (lay-flat plus
  // any tap/flip state). Animations write to this, and physicsWorld's
  // syncMeshFromBody composes it with body.quaternion every frame so
  // physics-driven tilt and animation-driven rotation can coexist without
  // overwriting each other. Initialize from the same formula that
  // computeTargetZ animates toward, so a card that spawns tapped or
  // face-down renders in the correct orientation on the first frame.
  mesh.userData = {
    type: 'card',
    cardInstance,
    intentEuler: new THREE.Euler(baseX, 0, baseZ, 'XYZ'),
    outline: strokeMesh,
  };

  return mesh;
}

function pileTopMaterial(pile, isSite, isCemetery) {
  if (isCemetery) {
    const topCard = pile.cards[pile.cards.length - 1];
    const imageUrl = topCard ? resolveLocalImageUrl(topCard.imageUrl) : null;
    if (imageUrl) {
      // Foil cemetery top cards render with the same board foil shader as
      // field cards so the holo survives on the graveyard (matching the
      // inspector / hand look). Mirrors createCardMesh's foil path: load the
      // cached SRGB board texture, then hand it to buildFoilShaderMaterial
      // which swaps in its own NoColorSpace foil map. Tagged so createPileMesh
      // / updatePileMesh can register it for sheen ticking and tear it down
      // (the foil's owned textures aren't in textureCache). If shader setup
      // throws, fall through to the plain lit material so a foil never blanks.
      const foiling = topCard.foiling || 'S';
      const isFoil = foiling === 'F' || foiling === 'R';
      if (isFoil) {
        const frontTexture = loadTexture(imageUrl);
        if (frontTexture) {
          try {
            // Use the card art's own alpha (rounded transparent corners) for the
            // box top face, exactly like the non-foil MeshStandardMaterial did,
            // so the foil corners round identically instead of a sharp rect.
            const foilMat = buildFoilShaderMaterial(frontTexture, imageUrl, foilZonesForCard(topCard), true);
            foilMat._isPileFoil = true;
            return foilMat;
          } catch (err) {
            console.warn('[cardMesh] pile foil ShaderMaterial setup failed, using fallback:', err?.message || err);
          }
        }
      }
      return new THREE.MeshStandardMaterial({ map: loadTexture(imageUrl), transparent: true, envMapIntensity: SCENE_ENV_MAP_INTENSITY });
    }
  }
  const backTexture = getBackTexture(isSite);
  return backTexture
    ? new THREE.MeshStandardMaterial({ map: backTexture, transparent: true, envMapIntensity: SCENE_ENV_MAP_INTENSITY })
    : new THREE.MeshStandardMaterial({ color: 0x1a1a2e });
}

// A pile's rendered slab COMPRESSES its cards. At full CARD_THICKNESS (a
// deliberate ~6x-real exaggeration so a lone card reads at table distance) a
// 60-card deck stood taller than a card is long. Cards keep their real
// thickness everywhere else (drag, landings, cell stacks); only the pile box
// uses this per-card contribution, floored at one card's thickness so a
// near-empty pile still reads as a stack.
const PILE_CARD_THICKNESS = 0.05 * CARD_SCALE;
export function pileThickness(count) {
  return Math.max(CARD_THICKNESS, count * PILE_CARD_THICKNESS);
}

export function createPileMesh(pile) {
  const count = pile.cards.length;
  if (count === 0) return null;
  // Banished and Collection are UI-only piles (opened via HUD buttons);
  // they never get a 3D mesh on the table.
  if (pile.name?.startsWith('Banished')) return null;
  if (pile.name === 'Collection') return null;

  const isSite = pile.name === 'Atlas';
  // Engine-built pile ids look like '${playerId}-cemetery'; legacy
  // ids look like 'cemetery-p1' / 'cemetery-p2'. Legacy names are
  // 'Cemetery P1' / 'Cemetery P2', engine names are plain 'Cemetery'.
  // Checking the name prefix catches both.
  const isCemetery = pile.name?.startsWith('Cemetery');
  const thickness = pileThickness(count);
  const geometry = new THREE.BoxGeometry(CARD_WIDTH, CARD_HEIGHT, thickness);
  const edgeMat = new THREE.MeshStandardMaterial({ color: CARD_EDGE_COLOR });

  // Cemetery piles show the top card face-up so both players can see
  // what's in the graveyard. All other piles show the card back.
  const topMat = pileTopMaterial(pile, isSite, isCemetery);

  // +Z = top of pile
  const materials = [edgeMat, edgeMat, edgeMat, edgeMat, topMat, edgeMat];
  const mesh = new THREE.Mesh(geometry, materials);

  mesh.rotation.x = -Math.PI / 2;
  let baseZ = 0;
  if (isSite) baseZ -= Math.PI / 2;
  if (pile.rotated) baseZ += Math.PI;
  mesh.rotation.z = baseZ;
  mesh.position.set(pile.x, thickness / 2, pile.z);

  mesh.castShadow = true;
  mesh.receiveShadow = false;
  mesh.userData = { type: 'pile', pile };

  // Register a foil cemetery top material so updateFoilSheens ticks its uTime
  // + view-angle uTilt each frame, same as field cards (the pile mesh is the
  // host so the tilt tracks the camera relative to the pile face).
  if (topMat._isPileFoil) {
    foilShaderEntries.push({ material: topMat, mesh });
    _foilMeshesDirty = true;
  }

  return mesh;
}

export function updatePileMesh(mesh, pile) {
  const count = pile.cards.length;
  if (count === 0) return false;

  const isSite = pile.name === 'Atlas';
  const isCemetery = pile.name?.startsWith('Cemetery');
  const thickness = pileThickness(count);

  mesh.geometry.dispose();
  mesh.geometry = new THREE.BoxGeometry(CARD_WIDTH, CARD_HEIGHT, thickness);

  const edgeMat = new THREE.MeshStandardMaterial({ color: CARD_EDGE_COLOR });
  const topMat = pileTopMaterial(pile, isSite, isCemetery);
  if (Array.isArray(mesh.material)) {
    mesh.material.forEach((m) => {
      // A foil cemetery top is a registered ShaderMaterial owning NoColorSpace
      // uTex/uFoilMap (not in textureCache). Unregister it from the sheen tick
      // registry and dispose its owned textures + material the same way a
      // removed field card's foil is cleaned up (disposeFoilEntry). updateFoil-
      // Sheens' prune loop can't catch it (the pile mesh is still attached), so
      // it must be torn down here on every rebuild to avoid a GPU/registry leak.
      if (m._isPileFoil) {
        const idx = foilShaderEntries.findIndex((e) => e.material === m);
        if (idx !== -1) {
          disposeFoilEntry(foilShaderEntries[idx]);
          foilShaderEntries.splice(idx, 1);
          _foilMeshesDirty = true;
        }
        return;
      }
      if (m.map && !textureCache.has(m.map.image?.src)) m.map.dispose();
      m.dispose();
    });
  }
  mesh.material = [edgeMat, edgeMat, edgeMat, edgeMat, topMat, edgeMat];
  if (topMat._isPileFoil) {
    foilShaderEntries.push({ material: topMat, mesh });
    _foilMeshesDirty = true;
  }

  mesh.position.y = thickness / 2;
  // Swap the pile reference in userData so click handlers don't end
  // up reading a stale pile.cards snapshot. Without this, clicking
  // a cemetery after a snapshot-driven pile rebuild gives you the
  // pile object from the moment the mesh was CREATED, not the
  // current one. The search dialog would then list pre-rebuild
  // contents while the pile mesh itself shows the up-to-date top
  // card.
  mesh.userData.pile = pile;
  return true;
}

const TOKEN_RADIUS = 1.75;
const TOKEN_HEIGHT = CARD_THICKNESS * 4;
const TOKEN_REST_Y = TOKEN_HEIGHT / 2 + 0.1;
const TOKEN_DRAG_Y = TOKEN_REST_Y + 2;
const TOKEN_COLORS = { red: 0xcc3333 };
// Matches the dark fill of the provided token SVGs so the cylinder side
// blends seamlessly into the textured top cap.
const TOKEN_SIDE_COLOR = 0x393939;

/**
 * Build materials for a token cylinder. When `topTextureUrl` is provided,
 * the returned array uses the SVG as the top-cap `map` and keeps the side
 * and bottom a solid dark color so only the printed face shows through.
 */
function createTokenMaterials({ sideColor = TOKEN_SIDE_COLOR, topTextureUrl } = {}) {
  if (!topTextureUrl) {
    return new THREE.MeshStandardMaterial({
      color: sideColor,
      roughness: 0.4,
      metalness: 0.1,
    });
  }
  const sideMat = new THREE.MeshStandardMaterial({ color: sideColor, roughness: 0.4, metalness: 0.1 });
  const topMat = new THREE.MeshStandardMaterial({
    map: loadTexture(topTextureUrl, null, { card: false }),
    color: 0xffffff,
    roughness: 0.4,
    metalness: 0.1,
    envMapIntensity: SCENE_ENV_MAP_INTENSITY,
    transparent: true,
  });
  const bottomMat = new THREE.MeshStandardMaterial({ color: sideColor, roughness: 0.4, metalness: 0.1 });
  // CylinderGeometry groups: 0 = side, 1 = top cap, 2 = bottom cap.
  return [sideMat, topMat, bottomMat];
}

// THREE's CylinderGeometry maps the top-cap UVs so that UV.x is aligned with
// the world +z axis, not +x. With our top-down camera that makes textures
// look rotated 90° on screen. Rotating the geometry around Y brings +x back
// into alignment with the texture's right edge so the printed dashes and
// elemental glyphs sit level with the table. Pass `flip: true` to add a
// further 180° spin so tokens on the opposite side of the table read
// right-side up for the player sitting across from p1.
const TOKEN_TOP_UV_ROTATION = Math.PI / 2;

function getTopCapRotation(hasTexture, flip) {
  if (!hasTexture) return 0;
  return TOKEN_TOP_UV_ROTATION + (flip ? Math.PI : 0);
}

export function createTokenMesh(tokenInstance) {
  const geometry = new THREE.CylinderGeometry(TOKEN_RADIUS, TOKEN_RADIUS, TOKEN_HEIGHT, 32);
  const rotation = getTopCapRotation(!!tokenInstance.topTexture, tokenInstance.flip);
  if (rotation) geometry.rotateY(rotation);
  const material = createTokenMaterials({
    sideColor: tokenInstance.topTexture ? TOKEN_SIDE_COLOR : (TOKEN_COLORS[tokenInstance.color] || TOKEN_COLORS.red),
    topTextureUrl: tokenInstance.topTexture,
  });
  const mesh = new THREE.Mesh(geometry, material);

  mesh.position.set(tokenInstance.x, TOKEN_REST_Y, tokenInstance.z);
  mesh.castShadow = true;
  mesh.receiveShadow = false;
  mesh.userData = { type: 'token', tokenInstance };

  return mesh;
}

/**
 * Create a flat cylindrical button for the game board (used by the tracker
 * +/- buttons). Takes a texture URL that is painted onto the top cap.
 * Pass `flip: true` to rotate the printed face 180° for the opposite seat.
 */
export function createTokenButtonMesh({ radius = TOKEN_RADIUS, height = 0.2, topTextureUrl, sideColor = TOKEN_SIDE_COLOR, flip = false } = {}) {
  const geometry = new THREE.CylinderGeometry(radius, radius, height, 32);
  const rotation = getTopCapRotation(!!topTextureUrl, flip);
  if (rotation) geometry.rotateY(rotation);
  const material = createTokenMaterials({ sideColor, topTextureUrl });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.receiveShadow = true;
  return mesh;
}

export function createTokenSpawnerMesh(x, z) {
  const geometry = new THREE.CylinderGeometry(TOKEN_RADIUS, TOKEN_RADIUS, TOKEN_HEIGHT, 32);
  const material = new THREE.MeshStandardMaterial({
    color: TOKEN_COLORS.red,
    roughness: 0.4,
    metalness: 0.1,
    transparent: true,
    opacity: 0.6,
  });
  const mesh = new THREE.Mesh(geometry, material);

  mesh.position.set(x, TOKEN_REST_Y, z);
  mesh.castShadow = false;
  mesh.receiveShadow = true;
  mesh.userData = { type: 'tokenSpawner' };

  return mesh;
}

// --- Minion stat tracker HUD ---

// Lucide-react v0.577 SVG path data for the Sword and Shield icons.
// Built once at module load and re-used for every tracker render so
// the canvas texture renders the same line-art as the DOM-rendered
// hover overlay (HoverStatusBadges). One shared icon vocabulary.
// Both icons are designed on a 24×24 viewBox with 2px stroke, round
// caps + joins. We scale that down to fit inside the tracker chip.
//
// The Sword path is the 4-subpath outline used by lucide v0.577+:
// blade body → top diagonal → cross-guard → pommel. (An earlier
// concatenation of the subpaths in this file used relative moveto
// commands that walked past the 24×24 viewBox, so the cross-guard
// and pommel rendered far off-icon and only the blade was visible.)
const SWORD_PATH_2D = new Path2D(
  'M14.5 17.5L3 6V3h3l11.5 11.5M13 19l6-6M16 16l4 4M19 21l2-2',
);
const SHIELD_PATH_2D = new Path2D(
  'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
);
// Lucide Footprints. Same icon used in the status ring menu's Movement
// row. Four subpaths in a single Path2D: left foot, right foot, and the
// two heel ground-lines.
const FOOTPRINTS_PATH_2D = new Path2D(
  'M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5.5c0 3.11-2 5.66-2 8.68V16a2 2 0 1 1-4 0Z '
  + 'M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.48-6-4.5-6C14.63 6 14 7.8 14 9.5c0 3.11 2 5.66 2 8.68V20a2 2 0 1 0 4 0Z '
  + 'M16 17h4 '
  + 'M4 13h4',
);

const STAT_COLORS_HUD = {
  atk: 'rgba(245,158,11,0.95)',
  hp: 'rgba(239,68,68,0.95)',
  mov: 'rgba(132,204,255,0.95)',
};

// Pre-baked surface textures for the in-canvas pill backgrounds. These
// are the same WebPs the codex / dialog panels layer via CSS background
// Pulling them into the CanvasTexture lets the in-3D chips share the
// codex's material language exactly: stone @ soft-light + scratches @
// overlay over a warm gradient.
//
// Loaded eagerly at module init so by the time the first card spawns
// the textures are usually decoded. If a card spawns before they're
// ready, `pillBgReady` returns false for that pass and the pill is
// drawn with the gradient alone. The next stat-change re-renders it
// with the textures applied.
const _PILL_BG_URLS = {
  stone: '/tex-stone.webp',
  scratches: '/tex-scratches.webp',
};
const _pillBgImages = {};
const _pillBgReadyCallbacks = new Set();
let _pillBgAllReady = false;
function _checkPillBgAllReady() {
  if (_pillBgAllReady) return;
  for (const key of Object.keys(_PILL_BG_URLS)) {
    if (!pillBgReady(key)) return;
  }
  _pillBgAllReady = true;
  // Notify subscribers (e.g. GameBoard) that pills painted before the
  // textures decoded are now eligible for a re-bake.
  for (const cb of _pillBgReadyCallbacks) cb();
  _pillBgReadyCallbacks.clear();
}
(function preloadPillBgImages() {
  for (const [key, url] of Object.entries(_PILL_BG_URLS)) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = _checkPillBgAllReady;
    img.src = url;
    _pillBgImages[key] = img;
  }
  // In case of HTTP cache hit, onload may have fired synchronously
  // before the listeners were wired. Check once more after the loop.
  _checkPillBgAllReady();
})();

function pillBgReady(key) {
  const img = _pillBgImages[key];
  return !!img && img.complete && img.naturalWidth > 0;
}

// Subscribe to the "stat-pill background textures are now decoded"
// signal. Fires once all WebPs are ready (immediately if they already
// are). Returns an unsubscribe function.
export function onPillBgReady(cb) {
  if (_pillBgAllReady) { cb(); return () => {}; }
  _pillBgReadyCallbacks.add(cb);
  return () => _pillBgReadyCallbacks.delete(cb);
}

function drawLucideIcon(ctx, path, cx, cy, size, color, strokeWidth = 2) {
  const s = size / 24;
  ctx.save();
  ctx.translate(cx - size / 2, cy - size / 2);
  ctx.scale(s, s);
  ctx.strokeStyle = color;
  // Visible stroke width = (lineWidth * s) = strokeWidth canvas px,
  // regardless of the target size.
  ctx.lineWidth = strokeWidth / s;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke(path);
  ctx.restore();
}

// Passive ATK / HP pill drawn into the card-local plane. No clickable
// regions. Value changes happen exclusively through the status ring
// (StatusRingMenu) on shift-click.
//
// Material recipe mirrors the codex / dialog panel: a solid dark
// base, stone @ soft-light + scratches @ overlay on top, then a gold
// rim with a thin bright accent stroke inside. No vertical gradient.
// the depth comes from the textures and the rim alone, same as the
// codex tome surface.
//
// Everything inside the rounded shape is fully opaque so the card
// art behind never bleeds through the chip.
// `delta` is the live stat minus the printed/base stat. A non-zero
// delta tints the pill surface. Green for a buff, red for a debuff.
// so players can tell at a glance that a unit's power or defense has
// drifted from the card's printed numbers. The tint persists as long
// as the unit is modified and clears the moment the value returns to
// the base (e.g. when HP resets at end of turn).
const STAT_HUD_W = 3.2;
const STAT_HUD_H = 2.0;
const STAT_PAD = 0.15;

function createStatTexture(stat, value, delta = 0) {
  const canvas = document.createElement('canvas');
  // 1.6:1 aspect so the pill reads as a compact rectangle rather than
  // a wide capsule. Matches STAT_HUD_W / STAT_HUD_H below.
  canvas.width = 384;
  canvas.height = 240;
  const ctx = canvas.getContext('2d');
  const w = 384;
  const h = 240;
  // The pill is inset equally from the card's bottom and side edges.
  // Subtracting that inset from the card radius makes the nearest pill
  // corner share the card corner's center: 0.75 - 0.15 = 0.60 world units.
  const pixelsPerWorldUnit = h / STAT_HUD_H;
  const r = (CARD_CORNER_RADIUS - STAT_PAD) * pixelsPerWorldUnit;

  const pillPath = () => {
    ctx.beginPath();
    ctx.roundRect(0, 0, w, h, r);
  };

  // Clip everything to the rounded shape so the full-canvas texture
  // patterns + rim stroke stay inside the pill outline (the bounding-
  // box corners must remain transparent so the material's alphaTest
  // discards them).
  ctx.save();
  pillPath();
  ctx.clip();

  // 1. Solid base. Same hex as CODEX_PANEL_BASE so the chip reads
  //    as a peer of the codex tome surface. No gradient.
  ctx.fillStyle = '#0c0a08';
  ctx.fillRect(0, 0, w, h);

  // 2. Stone texture @ soft-light. The dominant "stony" tone of the
  //    codex's CONTENT_BG_DEFAULT. The soft-light blend keeps the
  //    base color in charge of luminance while the stone adds the
  //    rocky surface variation.
  if (pillBgReady('stone')) {
    ctx.globalCompositeOperation = 'soft-light';
    ctx.globalAlpha = 1;
    const stonePat = ctx.createPattern(_pillBgImages.stone, 'repeat');
    if (stonePat) {
      ctx.fillStyle = stonePat;
      ctx.fillRect(0, 0, w, h);
    }
  }

  // 3. Scratches @ overlay. Adds the worn-metal grit. Lower
  //    intensity so the surface doesn't get noisy at distance.
  if (pillBgReady('scratches')) {
    ctx.globalCompositeOperation = 'overlay';
    ctx.globalAlpha = 0.5;
    const scratchPat = ctx.createPattern(_pillBgImages.scratches, 'repeat');
    if (scratchPat) {
      ctx.fillStyle = scratchPat;
      ctx.fillRect(0, 0, w, h);
    }
  }
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;

  // 3b. Buff / debuff tint. `screen` lifts the near-black base into a
  //     gentle hue without painting over the surface. Alpha stays
  //     low so the dark medieval pill is still clearly the dominant
  //     colour and the tint only nudges it.
  if (delta !== 0) {
    const tintAlpha = Math.min(0.32, 0.2 + Math.abs(delta) * 0.02);
    ctx.globalCompositeOperation = 'screen';
    ctx.globalAlpha = tintAlpha;
    ctx.fillStyle = delta > 0 ? '#2f9b4f' : '#a82a2a';
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
  }

  // 4. Rim. Gold by default, biased slightly toward the buff hue
  //    so the pill outline echoes the surface without losing its
  //    medieval gold character.
  const rimColor = delta > 0
    ? 'rgba(170, 165, 75, 0.95)'
    : delta < 0
      ? 'rgba(190, 130, 70, 0.95)'
      : 'rgba(180, 140, 60, 0.95)';
  ctx.strokeStyle = rimColor;
  ctx.lineWidth = 10;
  pillPath();
  ctx.stroke();

  // 5. Thin bright accent stroke inside the rim. The bevel cue. A
  //    single-line highlight, not a gradient, so the pill stays flat-
  //    textured rather than reading as a domed chip.
  const accentInset = 7;
  ctx.strokeStyle = 'rgba(255, 222, 152, 0.32)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.roundRect(
    accentInset,
    accentInset,
    w - accentInset * 2,
    h - accentInset * 2,
    r - accentInset,
  );
  ctx.stroke();

  ctx.restore();

  // 6. Icon (sword / shield) + value text. Outside the clip so the
  //    text drop shadow + icon stroke aren't restricted by it.
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const path = stat === 'atk'
    ? SWORD_PATH_2D
    : stat === 'mov'
      ? FOOTPRINTS_PATH_2D
      : SHIELD_PATH_2D;
  const color = STAT_COLORS_HUD[stat] || STAT_COLORS_HUD.hp;
  drawLucideIcon(ctx, path, w * 0.28, h / 2, 120, color, 6);

  // Value with a soft drop shadow for legibility on any card art
  // that might peek around the icon. Text fill tints with the buff
  // state. Far more eye-catching than the surface wash alone.
  ctx.shadowColor = 'rgba(0, 0, 0, 0.65)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 3;
  ctx.fillStyle = delta > 0
    ? '#ecf0c4'
    : delta < 0
      ? '#f5dab2'
      : '#f5e4b6';
  ctx.font = 'bold 140px sans-serif';
  // The movement chip surfaces the bonus directly (e.g. "+2" / "-1"),
  // not the absolute step count. It's an at-a-glance indicator of
  // how far the unit's mobility has drifted from its printed baseline.
  const display = stat === 'mov' && value > 0 ? `+${value}` : String(value);
  ctx.fillText(display, w * 0.66, h / 2 + 6);
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  const tex = new THREE.CanvasTexture(canvas);
  // Anisotropic filtering keeps the chip sharp at glancing camera
  // angles. The renderer clamps to its own GPU max if this exceeds
  // the hardware capability.
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

// The movement chip is a satellite of the defense pill. Smaller so it
// reads as a secondary modifier glance, anchored to the same right edge
// of the card and stacked directly above the defense chip.
const MOV_HUD_SCALE = 0.7;
const MOV_HUD_W = STAT_HUD_W * MOV_HUD_SCALE;
const MOV_HUD_H = STAT_HUD_H * MOV_HUD_SCALE;
const MOV_HUD_GAP = 0.2;
// Stat HUD and status badges sit just barely above the card's top face
// (card-local +Z). Used to be +0.15, which floated the widgets ~half a
// card-thickness above the surface. When a second card stacked on top
// (centers spaced by CARD_THICKNESS), the lower card's trackers ended
// up inside the upper card's volume. They were still *depth-tested*
// behind the upper card in theory, but the card art uses
// `alphaTest: 0.5`, so pixels below the alpha threshold (rounded
// corners, semi-transparent top rail) write no depth and the trackers
// bled through those holes. Keeping the z-offset small leaves the
// trackers well below the next card's bottom face so opaque occlusion
// wins everywhere.
const STAT_Z = CARD_THICKNESS / 2 + 0.01;

// Whether the pill needs to be counter-rotated so it reads upright
// from the local player's POV. `cardInstance.rotated` is the absolute
// "this card belongs to P2" flag; the local camera orbits to theta=π
// when the local player IS P2 (see tableScene.setOrbitTheta), which
// cancels the card's +π rotation. So the pill appears upside-down
// exactly when the card's rotated-ness doesn't match the local
// camera flip. I.e. `rotated !== !isHost`.
function pillNeedsFlip(cardInstance, isHost) {
  return !!cardInstance.rotated !== !isHost;
}

// Pills always sit at the card's printed-bottom corners. Same place
// a real Sorcery card prints power / defense. We don't mirror their
// position for opponent cards; on a rotated card the printed-bottom
// happens to be the visible-top from the local POV, which is exactly
// what the user wants ("stats stay at the bottom of the card"). Only
// the in-pill rotation flips so the text still reads upright.
function applyPillTransforms(atkMesh, hpMesh, movMesh, cardInstance, isHost) {
  const flip = pillNeedsFlip(cardInstance, isHost);
  const yPos = -CARD_HEIGHT / 2 + STAT_HUD_H / 2 + STAT_PAD;
  const atkLocalX = -CARD_WIDTH / 2 + STAT_HUD_W / 2 + STAT_PAD;
  const hpLocalX = CARD_WIDTH / 2 - STAT_HUD_W / 2 - STAT_PAD;

  atkMesh.position.set(atkLocalX, yPos, STAT_Z);
  atkMesh.rotation.z = flip ? Math.PI : 0;
  if (hpMesh) {
    hpMesh.position.set(hpLocalX, yPos, STAT_Z);
    hpMesh.rotation.z = flip ? Math.PI : 0;
  }
  if (movMesh) {
    // Right-edge aligned with the DEF chip, stacked just above it.
    const movLocalX = CARD_WIDTH / 2 - MOV_HUD_W / 2 - STAT_PAD;
    const movLocalY = yPos + STAT_HUD_H / 2 + MOV_HUD_GAP + MOV_HUD_H / 2;
    movMesh.position.set(movLocalX, movLocalY, STAT_Z);
    movMesh.rotation.z = flip ? Math.PI : 0;
  }
}

export function createLifeHUD(cardInstance, isHost = true, { atkOnly = false } = {}) {
  const hp = cardInstance.currentLife ?? 0;
  const atk = cardInstance.currentAttack ?? 0;
  const movBonus = cardInstance._movementBonus || 0;
  const atkDelta = atk - (cardInstance.baseAttack ?? atk);
  const hpDelta = hp - (cardInstance.baseLife ?? hp);

  // Same material recipe as the status badges (the only other in-card
  // transparent overlay in this file). They've been the known-good
  // reference for "lives on top of the card surface without z-fight
  // flicker." `alphaTest: 0.01` rejects the canvas's empty corner
  // pixels so they don't smear the card art underneath; polygonOffset
  // pulls the visible pixels in front of the card face.
  const hudMatProps = {
    transparent: true,
    alphaTest: 0.01,
    side: THREE.DoubleSide,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  };

  const atkGeo = new THREE.PlaneGeometry(STAT_HUD_W, STAT_HUD_H);
  const atkMat = new THREE.MeshBasicMaterial({ ...hudMatProps, map: createStatTexture('atk', atk, atkDelta) });
  const atkMesh = new THREE.Mesh(atkGeo, atkMat);
  atkMesh.userData = { type: 'lifeHUD', cardId: cardInstance.id, stat: 'atk' };
  atkMesh.renderOrder = 8;

  let hpMesh = null;
  let movMesh = null;
  if (!atkOnly) {
    const hpGeo = new THREE.PlaneGeometry(STAT_HUD_W, STAT_HUD_H);
    const hpMat = new THREE.MeshBasicMaterial({ ...hudMatProps, map: createStatTexture('hp', hp, hpDelta) });
    hpMesh = new THREE.Mesh(hpGeo, hpMat);
    hpMesh.userData = { type: 'lifeHUD', cardId: cardInstance.id, stat: 'hp' };
    // The card meshes are assigned a per-frame `renderOrder = i` based
    // on stack sort position (see GameBoard._applyComputedY). Children
    // don't inherit that. Pin the pills well above any conceivable card
    // index so the transparent pass draws them *after* their host card
    // and the depth-test alone decides visibility. Below the ownership-
    // outline strokeMesh's renderOrder = 10 so the outline still wins.
    hpMesh.renderOrder = 8;

    const movGeo = new THREE.PlaneGeometry(MOV_HUD_W, MOV_HUD_H);
    const movMat = new THREE.MeshBasicMaterial({ ...hudMatProps, map: createStatTexture('mov', movBonus, movBonus) });
    movMesh = new THREE.Mesh(movGeo, movMat);
    movMesh.userData = { type: 'lifeHUD', cardId: cardInstance.id, stat: 'mov' };
    movMesh.renderOrder = 8;
    movMesh.visible = movBonus !== 0;
  }

  applyPillTransforms(atkMesh, hpMesh, movMesh, cardInstance, isHost);

  return {
    sprite: atkMesh, // keep 'sprite' key for compat. Used for ATK display
    hpSprite: hpMesh,
    movSprite: movMesh,
  };
}

// Re-apply the per-card pill orientation. Call this after a card
// changes ownership (cardInstance.rotated flipped) so the pill keeps
// reading upright from the local POV.
export function updateLifeHUDOrientation(hud, cardInstance, isHost = true) {
  if (!hud) return;
  applyPillTransforms(hud.sprite, hud.hpSprite, hud.movSprite, cardInstance, isHost);
}

export function updateLifeHUD(mesh, value, stat = 'hp', delta = 0) {
  if (!mesh) return;
  if (mesh.material.map) mesh.material.map.dispose();
  mesh.material.map = createStatTexture(stat, value, delta);
  mesh.material.needsUpdate = true;
  if (stat === 'mov') mesh.visible = value !== 0;
}

// ── Counter token ────────────────────────────────────────────────
//
// A circular medieval token pinned to the centre of a card that tracks
// level counters (The Immortal Throne). Shares the stat-pill material
// language. Stone @ soft-light + scratches @ overlay over a near-black
// base, gold rim. But reads as a round token rather than a stat chip so
// it never reads as ATK/DEF. The number always reads upright for the
// local viewer via the same pillNeedsFlip orientation the stat pills use.

const COUNTER_HUD_D = CARD_WIDTH * 0.42;

function createCounterTexture(value) {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  const s = 256;
  const cx = s / 2;
  const cy = s / 2;
  const rad = s / 2 - 8;

  const disc = () => {
    ctx.beginPath();
    ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  };

  ctx.save();
  disc();
  ctx.clip();

  // Same surface recipe as the stat pills: solid base, stone @ soft-light,
  // scratches @ overlay (drawn only once the WebPs have decoded).
  ctx.fillStyle = '#0c0a08';
  ctx.fillRect(0, 0, s, s);
  if (pillBgReady('stone')) {
    ctx.globalCompositeOperation = 'soft-light';
    const stonePat = ctx.createPattern(_pillBgImages.stone, 'repeat');
    if (stonePat) { ctx.fillStyle = stonePat; ctx.fillRect(0, 0, s, s); }
  }
  if (pillBgReady('scratches')) {
    ctx.globalCompositeOperation = 'overlay';
    ctx.globalAlpha = 0.5;
    const scratchPat = ctx.createPattern(_pillBgImages.scratches, 'repeat');
    if (scratchPat) { ctx.fillStyle = scratchPat; ctx.fillRect(0, 0, s, s); }
  }
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  ctx.restore();

  // Gold rim + thin inner bevel accent, matching the pills.
  disc();
  ctx.strokeStyle = 'rgba(180, 140, 60, 0.95)';
  ctx.lineWidth = 12;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, rad - 9, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(255, 222, 152, 0.32)';
  ctx.lineWidth = 2;
  ctx.stroke();

  // Centred number with a soft drop shadow for legibility over card art.
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(0, 0, 0, 0.65)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 3;
  ctx.fillStyle = '#f5e4b6';
  ctx.font = 'bold 150px serif';
  ctx.fillText(String(value), cx, cy + 6);

  const tex = new THREE.CanvasTexture(canvas);
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

function counterFlip(cardInstance, isHost) {
  // Site cards are laid landscape (-90°); counter that so the token reads
  // upright, then add the P2-viewer 180° flip the stat pills use.
  return (cardInstance.isSite ? Math.PI / 2 : 0)
    + (pillNeedsFlip(cardInstance, isHost) ? Math.PI : 0);
}

export function createCounterHUD(cardInstance, isHost = true) {
  const geo = new THREE.PlaneGeometry(COUNTER_HUD_D, COUNTER_HUD_D);
  const mat = new THREE.MeshBasicMaterial({
    map: createCounterTexture(cardInstance._counters || 0),
    transparent: true,
    alphaTest: 0.01,
    side: THREE.DoubleSide,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.userData = { type: 'counterHUD', cardId: cardInstance.id };
  mesh.position.set(0, 0, STAT_Z);
  mesh.rotation.z = counterFlip(cardInstance, isHost);
  mesh.renderOrder = 9;
  return mesh;
}

export function updateCounterHUD(mesh, value) {
  if (!mesh) return;
  if (mesh.material.map) mesh.material.map.dispose();
  mesh.material.map = createCounterTexture(value);
  mesh.material.needsUpdate = true;
}

export function updateCounterHUDOrientation(mesh, cardInstance, isHost = true) {
  if (!mesh) return;
  mesh.rotation.z = counterFlip(cardInstance, isHost);
}

// ── Status effect badges ─────────────────────────────────────────
//
// Small circular badges attached to the card mesh in the top-left
// corner, stacking downward. Each badge shows a short abbreviation
// for the keyword ability (Stealth → STL, Ward → WRD, etc.) on a
// tinted background.

export const STATUS_EFFECTS = [
  { key: 'stealth',     label: k('Stealth'),     abbr: 'STL', color: '#6366f1' },
  { key: 'ward',        label: k('Ward'),        abbr: 'WRD', color: '#06b6d4' },
  { key: 'airborne',    label: k('Airborne'),    abbr: 'AIR', color: '#a0badb' },
  { key: 'charge',      label: k('Charge'),      abbr: 'CHG', color: '#f59e0b' },
  { key: 'lethal',      label: k('Lethal'),      abbr: 'LTH', color: '#ef4444' },
  { key: 'disabled',    label: k('Disabled'),    abbr: 'DIS', color: '#525252' },
  { key: 'silenced',    label: k('Silenced'),    abbr: 'SIL', color: '#a21caf' },
  { key: 'immobile',    label: k('Immobile'),    abbr: 'IMM', color: '#78716c' },
  { key: 'unmovable',   label: k('Unmovable'),   abbr: 'UNM', color: '#a8a29e' },
  { key: 'burrowing',   label: k('Burrowing'),   abbr: 'BUR', color: '#92400e' },
  { key: 'submerge',    label: k('Submerge'),    abbr: 'SUB', color: '#0891b2' },
  { key: 'voidwalk',    label: k('Voidwalk'),    abbr: 'VDW', color: '#7c3aed' },
  { key: 'spellcaster', label: k('Spellcaster'), abbr: 'SPC', color: '#c084fc' },
  { key: 'deathrite',   label: k('Deathrite'),   abbr: 'DTH', color: '#64748b' },
  { key: 'landbound',   label: k('Landbound'),   abbr: 'LND', color: '#a3a3a3' },
  { key: 'waterbound',  label: k('Waterbound'),  abbr: 'WTR', color: '#38bdf8' },
  { key: 'flooded',     label: k('Flooded'),     abbr: 'FLD', color: '#0284c7' },
  { key: 'ranged',      label: k('Ranged'),      abbr: 'RNG', color: '#fb923c' },
  { key: 'branded',     label: k('Branded'),     abbr: 'BRD', color: '#b91c1c' },
  // Lance is equipment-derived, never a `statuses` entry: the ring
  // toggle equips/removes a Lance artifact token (the combat engine
  // reads Lance from equippedCards), and its lit state reads the same
  // array. See equipActions.toggleLanceEquip.
  { key: 'lance',       label: k('Lance'),       abbr: 'LNC', color: '#c2884a' },
  // Summoning sickness is DERIVED (engine hasSummoningSickness: stamped on
  // realm entry, cleared at the controller's next turn, waived by Charge),
  // never a `statuses` entry. `derived` keeps it out of the manual status
  // ring; it still gets the badge, the hover pill and a 3D effect so a
  // fresh minion visibly reads as unable to move or attack.
  { key: 'summoning_sick', label: k('Summoning Sickness'), abbr: 'ZZZ', color: '#8b7ab8', derived: true },
];

// The manually toggleable subset (status ring / drawer). Derived statuses
// are read off the card state and cannot be switched by hand.
export const TOGGLEABLE_STATUS_EFFECTS = STATUS_EFFECTS.filter((e) => !e.derived);

// `STATUS_BADGE_SIZE` is the world-unit step used to position
// neighbouring badges along a stack. `STATUS_BADGE_VISIBLE` is the
// drawn quad edge, set slightly smaller so adjacent badges keep an
// air-gap between them rather than touching edge-to-edge.
const STATUS_BADGE_SIZE = 2.0;     // world-unit step
const STATUS_BADGE_VISIBLE = 1.6;  // world-unit drawn quad size
const STATUS_BADGE_GAP = 0.3;      // extra gap between stacked badges
// High-res canvas so the abbreviation text renders crisp even when the
// camera is close. 256 px for a 1-world-unit plane is ~23 px/unit on a
// typical game-board zoom. The same density as the stat HUD canvases.
const STATUS_BADGE_CANVAS = 256;

function createStatusBadgeTexture(abbr, color) {
  const s = STATUS_BADGE_CANVAS;
  const canvas = document.createElement('canvas');
  canvas.width = s;
  canvas.height = s;
  const ctx = canvas.getContext('2d');

  ctx.clearRect(0, 0, s, s);
  const r = s / 2 - 6;
  const cx = s / 2;
  const cy = s / 2;

  // ── Solid base with radial gradient for a 3D dome look ──
  const baseGrad = ctx.createRadialGradient(
    cx - r * 0.25, cy - r * 0.3, r * 0.1,
    cx, cy, r,
  );
  baseGrad.addColorStop(0, '#3a3530');     // highlight center (top-left)
  baseGrad.addColorStop(0.55, '#201c18');  // mid body
  baseGrad.addColorStop(1, '#0e0c0a');     // dark rim
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = baseGrad;
  ctx.fill();

  // ── Color gradient overlay. Concentrated at the top for a "lit" feel ──
  const colorGrad = ctx.createLinearGradient(cx, cy - r, cx, cy + r);
  colorGrad.addColorStop(0, color);
  colorGrad.addColorStop(0.6, color);
  colorGrad.addColorStop(1, 'transparent');
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = colorGrad;
  ctx.globalAlpha = 0.4;
  ctx.fill();
  ctx.globalAlpha = 1;

  // ── Top-edge specular highlight. "glass dome" sheen ──
  const sheenGrad = ctx.createLinearGradient(cx, cy - r, cx, cy - r * 0.15);
  sheenGrad.addColorStop(0, 'rgba(255,255,255,0.35)');
  sheenGrad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r - 1, 0, Math.PI * 2);
  ctx.clip();
  ctx.beginPath();
  ctx.ellipse(cx, cy - r * 0.45, r * 0.7, r * 0.4, 0, 0, Math.PI * 2);
  ctx.fillStyle = sheenGrad;
  ctx.fill();
  ctx.restore();

  // ── Bottom shadow crescent. Grounds the badge ──
  const shadowGrad = ctx.createLinearGradient(cx, cy + r * 0.3, cx, cy + r);
  shadowGrad.addColorStop(0, 'rgba(0,0,0,0)');
  shadowGrad.addColorStop(1, 'rgba(0,0,0,0.4)');
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r - 1, 0, Math.PI * 2);
  ctx.clip();
  ctx.beginPath();
  ctx.ellipse(cx, cy + r * 0.5, r * 0.8, r * 0.45, 0, 0, Math.PI * 2);
  ctx.fillStyle = shadowGrad;
  ctx.fill();
  ctx.restore();

  // ── Gold rim with inner + outer ring for depth ──
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(140,110,50,0.9)';
  ctx.lineWidth = 5;
  ctx.stroke();
  // Bright inner ring
  ctx.beginPath();
  ctx.arc(cx, cy, r - 2.5, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(220,180,80,0.35)';
  ctx.lineWidth = 1;
  ctx.stroke();

  // ── Abbreviation text ──
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${Math.round(s * 0.30)}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(0,0,0,0.8)';
  ctx.shadowBlur = 6;
  ctx.shadowOffsetY = 2;
  ctx.fillText(abbr, cx, cy + 2);
  ctx.shadowColor = 'transparent';

  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

const _badgeTexCache = new Map();
function getCachedBadgeTexture(abbr, color) {
  const key = `${abbr}-${color}`;
  if (_badgeTexCache.has(key)) return _badgeTexCache.get(key);
  const tex = createStatusBadgeTexture(abbr, color);
  _badgeTexCache.set(key, tex);
  return tex;
}

export function createStatusBadgeMesh(statusKey) {
  const def = STATUS_EFFECTS.find((e) => e.key === statusKey);
  if (!def) return null;
  // Flat plane parented to the card (same pattern as the ATK/HP
  // LifeHUD just above). Sprites were a trap here: they always face
  // the camera, so any camera tilt pushes half the quad into world-Z
  // values below the card's top surface and the depth test against
  // the card mesh kills that half (or, with `sizeAttenuation: true`,
  // the entire badge once the camera tilts enough). A plane parented
  // to the card stays parallel to the card surface and only needs a
  // small polygon-offset bias to win the depth test cleanly.
  const geo = new THREE.PlaneGeometry(STATUS_BADGE_VISIBLE, STATUS_BADGE_VISIBLE);
  const mat = new THREE.MeshBasicMaterial({
    map: getCachedBadgeTexture(def.abbr, def.color),
    transparent: true,
    alphaTest: 0.01,
    side: THREE.DoubleSide,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.userData = { type: 'statusBadge', statusKey };
  return mesh;
}

// Rebuild all status badge meshes for a card. Returns an array of the
// new meshes (caller adds them to the card mesh and tracks them).
// `isSite` flips the layout: site cards are rotated -90° around Z, so
// badges are positioned at what becomes the visual top-left of the
// horizontal card after rotation. Local bottom-left, stacking in +X
// (which maps to visual downward after the rotation).
export function buildStatusBadges(activeStatuses, isSite = false) {
  const badges = [];
  const pad = STAT_PAD + 0.1;
  const step = STATUS_BADGE_SIZE + STATUS_BADGE_GAP;

  let i = 0;
  for (const key of activeStatuses) {
    const mesh = createStatusBadgeMesh(key);
    if (!mesh) continue;
    if (isSite) {
      // Bottom-left of portrait card → visual top-left of horizontal site.
      // Counter-rotate +90° around Z so the badge text reads upright on
      // the horizontal card (the card itself is rotated -90°).
      mesh.position.set(
        -CARD_WIDTH / 2 + STATUS_BADGE_SIZE / 2 + pad + i * step,
        -CARD_HEIGHT / 2 + STATUS_BADGE_SIZE / 2 + pad,
        STAT_Z,
      );
      mesh.rotation.z = Math.PI / 2;
    } else {
      // Top-left of portrait card, stacking downward
      mesh.position.set(
        -CARD_WIDTH / 2 + STATUS_BADGE_SIZE / 2 + pad,
        CARD_HEIGHT / 2 - STATUS_BADGE_SIZE / 2 - pad - i * step,
        STAT_Z,
      );
    }
    badges.push(mesh);
    i++;
  }
  return badges;
}

/**
 * Swap a card mesh's front texture. Used when an opponent reveals a
 * face-down card they had previously placed with sanitized identity.
 * the mesh was built with a card-back stand-in as its front map, and
 * now needs to show the real art. Safe to call with a mesh that
 * already has the real art; it'll just reload the same texture.
 */
export function updateCardFrontTexture(mesh, imageUrl) {
  if (!mesh) return;
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  const frontMat = mats[0];
  if (!frontMat) return;
  const resolved = resolveLocalImageUrl(imageUrl);
  if (!resolved) return;
  frontMat.map = loadTexture(resolved, spellbookBackTexture);
  frontMat.needsUpdate = true;
}

export function disposeTextureCache() {
  for (const tex of textureCache.values()) tex.dispose();
  textureCache.clear();
  spellbookBackTexture = null;
  atlasBackTexture = null;
}

/**
 * Create a face-down card mesh for the opponent's visible hand on the
 * table. Shows only the card back (spellbook or atlas). The card never
 * reveals its face, it's purely a "how many cards does the opponent hold"
 * indicator. Not interactive and has no physics body.
 */
export function createHandBackMesh(isSite, forceSpellbook = false) {
  const geometry = new THREE.BoxGeometry(CARD_WIDTH, CARD_HEIGHT, CARD_THICKNESS);
  const edgeMat = new THREE.MeshStandardMaterial({ color: CARD_EDGE_COLOR });
  const backTexture = getBackTexture(isSite, forceSpellbook);
  const faceMat = backTexture
    ? new THREE.MeshStandardMaterial({ map: backTexture, transparent: true, envMapIntensity: SCENE_ENV_MAP_INTENSITY })
    : new THREE.MeshStandardMaterial({ color: 0x1a1a2e });

  // +Z = top face (visible when laid flat). Both top and bottom show the
  // card back so the mesh reads correctly from any camera angle.
  const materials = [edgeMat, edgeMat, edgeMat, edgeMat, faceMat, faceMat];
  const mesh = new THREE.Mesh(geometry, materials);

  mesh.castShadow = true;
  mesh.receiveShadow = false;
  mesh.userData = { type: 'opponentHand' };

  return mesh;
}

// Revealed opponent-hand mesh. Face-up variant used when the other
// player has explicitly "shown" a card. Top face shows the card art;
// bottom face keeps the back texture so it still reads correctly from
// any camera angle. Tagged `opponentHandFace` so hover/click/inspect
// can pick it up. The bare-back variant stays non-interactive.
export function createHandFaceMesh(cardData) {
  const { imageUrl, isSite } = cardData;
  const geometry = new THREE.BoxGeometry(CARD_WIDTH, CARD_HEIGHT, CARD_THICKNESS);
  const edgeMat = new THREE.MeshStandardMaterial({ color: CARD_EDGE_COLOR });
  const faceTexture = imageUrl ? loadTexture(resolveLocalImageUrl(imageUrl)) : null;
  const frontMat = faceTexture
    ? new THREE.MeshStandardMaterial({ map: faceTexture, transparent: true, alphaTest: 0.5, envMapIntensity: SCENE_ENV_MAP_INTENSITY })
    : new THREE.MeshStandardMaterial({ color: 0x1a1a2e });
  const backTexture = getBackTexture(isSite, !!cardData._spellbookBack);
  const backMat = backTexture
    ? new THREE.MeshStandardMaterial({ map: backTexture, transparent: true, envMapIntensity: SCENE_ENV_MAP_INTENSITY })
    : new THREE.MeshStandardMaterial({ color: 0x1a1a2e });

  const materials = [edgeMat, edgeMat, edgeMat, edgeMat, frontMat, backMat];
  const mesh = new THREE.Mesh(geometry, materials);

  mesh.castShadow = true;
  mesh.receiveShadow = false;
  mesh.userData = { type: 'opponentHandFace', cardInstance: cardData };

  return mesh;
}

export { CARD_WIDTH, CARD_HEIGHT, CARD_THICKNESS, TOKEN_CARD_SCALE, TOKEN_RADIUS, TOKEN_HEIGHT, TOKEN_REST_Y, TOKEN_DRAG_Y };
// In-card HUD geometry (world units), shared with the DOM replica in
// components/gameBoard/BoardCardOverlays.jsx so thumbnails match the table.
export { STAT_HUD_W, STAT_HUD_H, STAT_PAD, MOV_HUD_SCALE, MOV_HUD_GAP, STATUS_BADGE_SIZE, STATUS_BADGE_VISIBLE, STATUS_BADGE_GAP };
